import React, { useEffect, useState } from 'react';
import { Tabs, Tab } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useLazyQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import SelectFormField from '@components/form-fields/SelectFormField';
import DatePickerFormField from '@components/form-fields/DatePickerFormField';
import NumberInputFormField from '@components/form-fields/NumberInputFormField';
import EnhancedButton from '@components/button/EnhancedButton';
import TabPanel from '@components/layout/tabs/TabPanel';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { getError, lookupListAsRecordObject } from '@utils/graph-utils';
import { modifyPerson, getZonesByCountry } from './queries';
import { ValueOf } from '@utils/helper-utils';
import { useParams } from 'react-router-dom';
import EnhancedInternationalPhoneInput from '@components/_old/form/EnhancedInternationalPhoneInput';
import { validateField, validateForm } from './validation';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { MAIN_THEME } from '@styles';
import { IAbstractRecord } from '@models';
import SwitchFormField from '@components/form-fields/SwitchFormField';
import { IPersonWidgetInfo } from '..';

interface IInsuredPersonPopUpFormProps {
  lineExternalCode: string;
  data: IPersonWidgetInfo[];
  maxInsured: number;
  lovs: Record<string, Record<string, string>>;
  onClose: () => void;
  onSuccess: () => void;
}

const useStyles = makeStyles()(() => ({
  content: {
    padding: '0 63px 40px 30px',
  },
  tabsRoot: {
    minHeight: 'auto',
    borderBottom: `1px solid ${MAIN_THEME.color.white92}`,
    marginBottom: '20px',
  },
  tabRoot: {
    textTransform: 'none',
    minWidth: 72,
    minHeight: 'auto',
    padding: '6px 12px',
    marginRight: '4px',
    color: MAIN_THEME.color.black47,
    fontFamily: MAIN_THEME.font.regular,
    '&.Mui-selected': {
      color: MAIN_THEME.color.black,
      fontFamily: MAIN_THEME.font.medium,
      backgroundColor: MAIN_THEME.color.white98,
    },
  },
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 23.5%)',
    gap: '2%',
    justifyContent: 'flex-start',
    alignItems: 'center',
    alignContent: 'center',
  },
  buttonsContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignContent: 'center',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '15px',
    marginTop: '3em',
  },
}));

const InsuredPersonPopUpForm: React.FC<IInsuredPersonPopUpFormProps> = ({
  lineExternalCode,
  data,
  maxInsured,
  lovs,
  onClose,
  onSuccess,
}) => {
  const { classes } = useStyles();
  const params = useParams();

  const [localLovs, setLocalLovs] = useState(lovs);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [policyPersons, setPolicyPersons] = useState<IPersonWidgetInfo[]>(data);
  const [errors, setErrors] = useState<Record<number, Record<string, string>>>(
    {}
  );

  const [modifyPersonAction] = useMutation(modifyPerson());
  const [getZonesByCountryLazy] = useLazyQuery(getZonesByCountry(), {
    fetchPolicy: 'no-cache',
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const handleFieldUpdate = (
    index: number,
    fieldName: keyof IPersonWidgetInfo,
    value: ValueOf<IPersonWidgetInfo>
  ) => {
    const newPersons = [...policyPersons];
    (newPersons[index] as IAbstractRecord)[fieldName] = value;
    setPolicyPersons(newPersons);
  };

  const handleFieldBlur = async (
    index: number,
    fieldName: keyof IPersonWidgetInfo
  ) => {
    if (fieldName === 'countryCode') {
      const countryCode = policyPersons[index].countryCode;
      if (countryCode) {
        try {
          const res = await getZonesByCountryLazy({
            variables: { countryID: countryCode },
          });
          const zonesData = res?.data?.Insurance?.lookups?.zones || [];
          const zonesLookup = lookupListAsRecordObject(zonesData);
          setLocalLovs((prev) => ({ ...prev, zones: zonesLookup }));
        } catch (error) {
          toast.error(<ToastErrorMessage>{getError(error)}</ToastErrorMessage>);
        }
      }
    }
    const value = (policyPersons[index] as IAbstractRecord)[fieldName];
    const errorMessage = validateField(fieldName, value, lineExternalCode);
    setErrors((prev) => ({
      ...prev,
      [index]: { ...prev[index], [fieldName]: errorMessage },
    }));
  };

  const initialize = async () => {
    const countryCode = policyPersons[selectedTab].countryCode;
    if (countryCode) {
      const result = await getZonesByCountryLazy({
        variables: { countryID: countryCode || null },
      });
      const zonesData = result?.data?.Insurance?.lookups?.zones || [];
      const zonesLookup = lookupListAsRecordObject(zonesData);
      setLocalLovs((prev) => ({ ...prev, zones: zonesLookup }));
    }
  };

  useEffect(() => {
    initialize();
  }, []);

  const handleSubmit = async () => {
    const { errors: newErrors, valid } = validateForm(
      policyPersons,
      lineExternalCode
    );
    setErrors(newErrors);
    if (!valid) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
      return;
    }
    try {
      const policyPersonInputs = policyPersons.map((person) => ({
        type: 'INSURED',
        orderNumber: person.order || null,
        percentage: person.percentage || null,
        relation: person.relation || null,
        cardNumber: person.cardNumber || null,
        exclusions: person.exclusions || null,
        remarks: person.remarks || null,
        grLimitDays: person.grLimitDays || null,
        continuity: person.continuity || null,
        tpaReference1: person.tpaReference1 || null,
        tpaReference2: person.tpaReference2 || null,
        limitOfCover: person.limitOfCover || null,
        smoker: person.smoker || false,
        fullName: person.fullName || null,
        phoneType: person.phoneType.toUpperCase() || null,
        phoneNumber: person.phoneNumber || null,
        isTaxable: person.isTaxable || false,
        occupationClass: person.occupationClassCode || null,
        profession: person.profession || null,
        country: person.countryCode || null,
        zone: person.zoneCode || null,
        city: person.city || null,
        street: person.street || null,
        building: person.building || null,
        addressDetails: person.addressDetails || null,
        addressDetailsArabic: person.addressDetailsArabic || null,
        nationalityOne: person.nationalityOneCode || null,
        dateOfBirth: person.dateOfBirth || null,
        gender: person.gender.toUpperCase() || null,
        passportNumber: person.passportNumber || null,
        personCode: person.personCode || null,
      }));
      await modifyPersonAction({
        variables: {
          entityId: params.id,
          personDetailsInputs: policyPersonInputs,
        },
      });
      toast.success(
        <ToastSuccessMessage>Policy updated successfully!</ToastSuccessMessage>
      );
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(<ToastErrorMessage>{getError(err)}</ToastErrorMessage>);
    }
  };

  const currentPerson = policyPersons[selectedTab];

  const showLimitOfCover = [
    '5',
    '9',
    '10',
    '13',
    '21',
    '35',
    '11',
    '15',
    '16',
    '25',
    '27',
    '29',
    '30',
    '31',
    '37',
    '38',
    '39',
    '42',
    '46',
  ].includes(lineExternalCode);

  const showAddisionalDetailsSection = [
    '19',
    '48',
    '5',
    '9',
    '10',
    '13',
    '21',
    '35',
    '11',
    '15',
    '16',
    '25',
    '27',
    '29',
    '30',
    '31',
    '37',
    '38',
    '39',
    '42',
    '46',
  ].includes(lineExternalCode);

  return (
    <div className={classes.content}>
      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        aria-label="Insured Person Tabs"
        classes={{ root: classes.tabsRoot }}
        TabIndicatorProps={{ style: { display: 'none' } }}
      >
        {policyPersons.map((_, index) => (
          <Tab
            key={`Person-${index}`}
            label={`Insured ${index + 1}`}
            classes={{ root: classes.tabRoot }}
          />
        ))}
      </Tabs>
      <TabPanel index={selectedTab} key={`insured-${selectedTab}`}>
        <WidgetSection title="General Details">
          <div className={classes.fieldRow}>
            <TextInputFormField
              name={`fullName-${selectedTab}`}
              title="Full Name*"
              placeholder="Full Name"
              value={currentPerson.fullName || ''}
              onChange={(e) =>
                handleFieldUpdate(selectedTab, 'fullName', e.target.value)
              }
              onBlur={() => handleFieldBlur(selectedTab, 'fullName')}
              error={errors[selectedTab]?.fullName || ''}
              disabled={true}
            />
            {['19', '48'].includes(lineExternalCode) && (
              <SelectFormField
                name={`relation-${selectedTab}`}
                title="Relation*"
                placeholder="Relation"
                value={currentPerson.relation || ''}
                onChange={(val: string) =>
                  handleFieldUpdate(selectedTab, 'relation', val)
                }
                onBlur={() => handleFieldBlur(selectedTab, 'relation')}
                error={errors[selectedTab]?.relation || ''}
                selectOptions={lovs.insuredRelations || {}}
                disabled={currentPerson.relation === 'PRINCIPAL'}
              />
            )}
            <DatePickerFormField
              name={`dateOfBirth-${selectedTab}`}
              title={
                ['43', '6', '19', '48'].includes(lineExternalCode)
                  ? 'Date of Birth*'
                  : 'Date of Birth'
              }
              value={currentPerson.dateOfBirth}
              onDateChange={(date) =>
                handleFieldUpdate(
                  selectedTab,
                  'dateOfBirth',
                  date ? date.toISOString() : ''
                )
              }
              onBlur={() => handleFieldBlur(selectedTab, 'dateOfBirth')}
              error={errors[selectedTab]?.dateOfBirth || ''}
              maxDate={new Date()}
              canClearDate={false}
              disabled={true}
            />
            <SelectFormField
              name={`phoneType-${selectedTab}`}
              title="Phone Type"
              placeholder="Phone Type"
              value={(currentPerson.phoneType || '').toUpperCase()}
              onChange={(val: string) =>
                handleFieldUpdate(selectedTab, 'phoneType', val)
              }
              selectOptions={lovs.phoneType || {}}
            />
            <EnhancedInternationalPhoneInput
              name="phoneNumber"
              title="Phone Number"
              placeholder="Phone Number"
              onChange={(val) =>
                handleFieldUpdate(selectedTab, 'phoneNumber', val)
              }
              onBlur={() => handleFieldBlur(selectedTab, 'phoneNumber')}
              value={currentPerson.phoneNumber || ''}
              error={errors[selectedTab]?.phoneNumber || ''}
              disableDropDown={false}
              editCountryCode={false}
              defaultCountry="lb"
            />
            <SelectFormField
              name={`gender-${selectedTab}`}
              title={
                ['43', '6', '19', '48'].includes(lineExternalCode)
                  ? 'Gender*'
                  : 'Gender'
              }
              placeholder="Gender"
              value={(currentPerson.gender || '').toUpperCase()}
              onChange={(val: string) =>
                handleFieldUpdate(selectedTab, 'gender', val)
              }
              onBlur={() => handleFieldBlur(selectedTab, 'gender')}
              error={errors[selectedTab]?.gender || ''}
              selectOptions={lovs.genders || {}}
              disabled={['43', '6', '19', '48'].includes(lineExternalCode)}
            />
            {lineExternalCode === '19' && (
              <SelectFormField
                name={`smoker-${selectedTab}`}
                title="Smoker"
                placeholder="Smoker"
                value={currentPerson.smoker ? 'YES' : 'NO'}
                onChange={(val: string) =>
                  handleFieldUpdate(selectedTab, 'smoker', val === 'YES')
                }
                selectOptions={{ YES: 'Yes', NO: 'No' }}
              />
            )}
            <TextInputFormField
              name={`passportNumber-${selectedTab}`}
              title="Passport Number"
              placeholder="Passport Number"
              value={currentPerson.passportNumber || ''}
              onChange={(e) =>
                handleFieldUpdate(selectedTab, 'passportNumber', e.target.value)
              }
            />
            <SelectFormField
              name={`nationalityOne-${selectedTab}`}
              title={lineExternalCode === '43' ? 'Nationality*' : 'Nationality'}
              placeholder="Nationality"
              value={currentPerson.nationalityOneCode || ''}
              onChange={(val: string) =>
                handleFieldUpdate(selectedTab, 'nationalityOneCode', val)
              }
              selectOptions={lovs.nationalities || {}}
              disabled={lineExternalCode === '43'}
            />
            {maxInsured && maxInsured > 1 && (
              <NumberInputFormField
                name={`order-${selectedTab}`}
                title="Order Number"
                placeholder="Order Number"
                value={currentPerson.order || ''}
                onChange={(e) =>
                  handleFieldUpdate(
                    selectedTab,
                    'order',
                    Number(e.target.value)
                  )
                }
                disabled={true}
              />
            )}
          </div>
        </WidgetSection>
        <WidgetSection title="Business Details">
          <div className={classes.fieldRow}>
            <SwitchFormField
              key={`taxable-${selectedTab}`}
              name={`taxable-${selectedTab}`}
              title="Taxable"
              value={currentPerson.isTaxable}
              onChange={(_, val) =>
                handleFieldUpdate(selectedTab, 'isTaxable', val)
              }
            />
            <SelectFormField
              name={`occupationClass-${selectedTab}`}
              title={
                lineExternalCode === '43'
                  ? 'Occupation Class*'
                  : 'Occupation Class'
              }
              placeholder="Occupation Class"
              value={currentPerson.occupationClassCode || ''}
              onChange={(val: string) =>
                handleFieldUpdate(selectedTab, 'occupationClassCode', val)
              }
              onBlur={() => handleFieldBlur(selectedTab, 'occupationClassCode')}
              error={errors[selectedTab]?.occupationClassCode || ''}
              selectOptions={lovs.occupationClasses || {}}
              disabled={lineExternalCode === '43'}
            />
            <TextInputFormField
              name={`profession-${selectedTab}`}
              title="Profession"
              placeholder="Profession"
              value={currentPerson.profession || ''}
              onChange={(e) =>
                handleFieldUpdate(selectedTab, 'profession', e.target.value)
              }
            />
          </div>
        </WidgetSection>
        {showAddisionalDetailsSection && (
          <WidgetSection title="Additional Details">
            <div className={classes.fieldRow}>
              {lineExternalCode === '19' && (
                <NumberInputFormField
                  name={`grLimitDays-${selectedTab}`}
                  title="GR Limit Days"
                  placeholder="GR Limit Days"
                  value={currentPerson.grLimitDays || ''}
                  onChange={(e) =>
                    handleFieldUpdate(
                      selectedTab,
                      'grLimitDays',
                      Number(e.target.value)
                    )
                  }
                />
              )}
              {['19', '48'].includes(lineExternalCode) && (
                <TextInputFormField
                  name={`cardNumber-${selectedTab}`}
                  title="Card Number"
                  placeholder="Card Number"
                  value={currentPerson.cardNumber || ''}
                  onChange={(e) =>
                    handleFieldUpdate(selectedTab, 'cardNumber', e.target.value)
                  }
                />
              )}
              {lineExternalCode === '19' && (
                <>
                  <SelectFormField
                    name={`continuity-${selectedTab}`}
                    title="Continuity"
                    placeholder="Continuity"
                    value={currentPerson.continuity || ''}
                    onChange={(val: string) =>
                      handleFieldUpdate(selectedTab, 'continuity', val)
                    }
                    selectOptions={lovs.continuityOptions || {}}
                  />
                  <TextInputFormField
                    name={`tpaReference1-${selectedTab}`}
                    title="TPA Reference 1"
                    placeholder="TPA Reference 1"
                    value={currentPerson.tpaReference1 || ''}
                    onChange={(e) =>
                      handleFieldUpdate(
                        selectedTab,
                        'tpaReference1',
                        e.target.value
                      )
                    }
                  />
                  <TextInputFormField
                    name={`tpaReference2-${selectedTab}`}
                    title="TPA Reference 2"
                    placeholder="TPA Reference 2"
                    value={currentPerson.tpaReference2 || ''}
                    onChange={(e) =>
                      handleFieldUpdate(
                        selectedTab,
                        'tpaReference2',
                        e.target.value
                      )
                    }
                  />
                </>
              )}
              {['19', '48'].includes(lineExternalCode) && (
                <>
                  <TextInputFormField
                    name={`exclusions-${selectedTab}`}
                    title="Exclusions"
                    placeholder="Exclusions"
                    value={currentPerson.exclusions || ''}
                    onChange={(e) =>
                      handleFieldUpdate(
                        selectedTab,
                        'exclusions',
                        e.target.value
                      )
                    }
                  />
                  <TextInputFormField
                    name={`remarks-${selectedTab}`}
                    title="Remarks"
                    placeholder="Remarks"
                    value={currentPerson.remarks || ''}
                    onChange={(e) =>
                      handleFieldUpdate(selectedTab, 'remarks', e.target.value)
                    }
                  />
                </>
              )}
            </div>
          </WidgetSection>
        )}
        {showLimitOfCover && (
          <WidgetSection title="Limit of Cover">
            <div className={classes.fieldRow}>
              <TextInputFormField
                name={`limitOfCover-${selectedTab}`}
                title="Limit of Cover"
                placeholder="Limit of Cover"
                value={currentPerson.limitOfCover || ''}
                onChange={(e) =>
                  handleFieldUpdate(selectedTab, 'limitOfCover', e.target.value)
                }
              />
            </div>
          </WidgetSection>
        )}
        <WidgetSection title="Address Details">
          <div className={classes.fieldRow}>
            <SelectFormField
              name={`country-${selectedTab}`}
              title="Country*"
              placeholder="Country"
              value={currentPerson.countryCode || ''}
              onChange={(val: string) =>
                handleFieldUpdate(selectedTab, 'countryCode', val)
              }
              onBlur={() => handleFieldBlur(selectedTab, 'countryCode')}
              error={errors[selectedTab]?.countryCode || ''}
              selectOptions={localLovs.countries || {}}
            />
            <SelectFormField
              name={`zone-${selectedTab}`}
              title="Zone"
              placeholder="Zone"
              value={currentPerson.zoneCode || ''}
              onChange={(val: string) =>
                handleFieldUpdate(selectedTab, 'zoneCode', val)
              }
              selectOptions={localLovs.zones || {}}
            />
            <TextInputFormField
              name={`city-${selectedTab}`}
              title="City*"
              placeholder="City"
              value={currentPerson.city || ''}
              onChange={(e) =>
                handleFieldUpdate(selectedTab, 'city', e.target.value)
              }
              onBlur={() => handleFieldBlur(selectedTab, 'city')}
              error={errors[selectedTab]?.city || ''}
            />
            <TextInputFormField
              name={`street-${selectedTab}`}
              title="Street"
              placeholder="Street"
              value={currentPerson.street || ''}
              onChange={(e) =>
                handleFieldUpdate(selectedTab, 'street', e.target.value)
              }
            />
            <TextInputFormField
              name={`building-${selectedTab}`}
              title="Building"
              placeholder="Building"
              value={currentPerson.building || ''}
              onChange={(e) =>
                handleFieldUpdate(selectedTab, 'building', e.target.value)
              }
            />
            <TextInputFormField
              name={`addressDetails-${selectedTab}`}
              title="Address Details"
              placeholder="Address Details"
              value={currentPerson.addressDetails || ''}
              onChange={(e) =>
                handleFieldUpdate(selectedTab, 'addressDetails', e.target.value)
              }
            />
            <TextInputFormField
              name={`addressDetailsArabic-${selectedTab}`}
              title="Address Details Arabic"
              placeholder="Address Details Arabic"
              value={currentPerson.addressDetailsArabic || ''}
              onChange={(e) =>
                handleFieldUpdate(
                  selectedTab,
                  'addressDetailsArabic',
                  e.target.value
                )
              }
            />
          </div>
        </WidgetSection>
      </TabPanel>
      <div className={classes.buttonsContainer}>
        <EnhancedButton onClick={onClose}>Cancel</EnhancedButton>
        <EnhancedButton isPrimary onClick={handleSubmit}>
          Submit
        </EnhancedButton>
      </div>
    </div>
  );
};

export default InsuredPersonPopUpForm;
